import { useNavigate } from 'react-router-dom';
import { ShieldOff, ArrowLeft, LogOut, Smartphone } from 'lucide-react';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuthStore } from '@/stores/auth-store';

const ROLE_LABELS: Record<string, string> = {
  client: 'Cliente',
  patrol: 'Patrullero',
  operator: 'Operador',
  admin: 'Administrador',
  super_admin: 'Super Admin',
};

/**
 * Wipe all Supabase auth tokens, app caches and service workers, then reload.
 * Useful when a confirmed email leaves the browser in a stale state.
 */
async function clearSessionAndReload(signOut: () => Promise<void>) {
  try { await signOut(); } catch { /* ignore */ }
  try {
    // Drop Supabase auth tokens and any other app keys.
    Object.keys(localStorage)
      .filter((k) => k.startsWith('sb-') || k.startsWith('asi-'))
      .forEach((k) => localStorage.removeItem(k));
    sessionStorage.clear();
  } catch { /* ignore */ }
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch { /* ignore */ }
  try {
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n)));
    }
  } catch { /* ignore */ }
  // Hard reload from server to bypass disk cache.
  window.location.replace('/login');
}

export function UnauthorizedPage() {
  const navigate = useNavigate();
  const { role } = usePermissions();
  const signOut  = useAuthStore((s) => s.signOut);

  const isClient = role === 'client';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-950 p-6">
      <div className="max-w-md w-full text-center">
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 rounded-full bg-red-100 dark:bg-red-500/20 flex items-center justify-center">
            {isClient
              ? <Smartphone className="w-10 h-10 text-blue-600 dark:text-blue-400" />
              : <ShieldOff   className="w-10 h-10 text-red-600  dark:text-red-400"  />}
          </div>
        </div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          {isClient ? 'Esta es la consola de operadores' : 'Acceso denegado'}
        </h1>
        <p className="text-gray-500 dark:text-slate-400 mb-2">
          {isClient
            ? 'Tu cuenta de cliente debe usarse desde la app móvil ASI Security.'
            : 'No tienes permisos para ver esta página.'}
        </p>
        {role && !isClient && (
          <p className="text-sm text-gray-400 dark:text-slate-500 mb-8">
            Tu rol actual es <span className="font-semibold text-gray-700 dark:text-slate-300">{ROLE_LABELS[role] ?? role}</span>.
            Contacta a un administrador si necesitas acceso.
          </p>
        )}
        {isClient && (
          <p className="text-sm text-gray-400 dark:text-slate-500 mb-8">
            Descarga la app o usa el dispositivo donde la tienes instalada para iniciar sesión.
          </p>
        )}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {!isClient && (
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Regresar
            </button>
          )}
          <button
            onClick={() => { void clearSessionAndReload(signOut); }}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-slate-200 dark:bg-slate-800 text-slate-800 dark:text-slate-100 rounded-lg hover:bg-slate-300 dark:hover:bg-slate-700 transition-colors font-medium"
          >
            <LogOut className="w-4 h-4" />
            Cerrar sesión y limpiar caché
          </button>
        </div>
      </div>
    </div>
  );
}

